import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { maskPhone } from "@/lib/phone";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { smartDueLabel, formatDate } from "@/lib/constants";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  PhoneOutgoing, PhoneCall, CalendarPlus, Clock, X, Search, AlarmClock,
  TrendingDown, CalendarCheck, Moon,
} from "lucide-react";

const TABS = [
  { value: "action", label: "Action Required" },
  { value: "advised", label: "Advised" },
  { value: "stalled", label: "Stalled" },
  { value: "snoozed", label: "Snoozed" },
  { value: "closed", label: "Closed" },
];

const REASON_LABEL = {
  advised_visit: "Advised visit",
  stalled_treatment: "Stalled",
};

// Tab -> query params. "Advised"/"Stalled" narrow the open list by reason
// rather than being separate statuses.
function paramsForTab(tab) {
  if (tab === "advised") return { status: "action", reason: "advised_visit" };
  if (tab === "stalled") return { status: "action", reason: "stalled_treatment" };
  if (tab === "snoozed") return { status: "snoozed" };
  if (tab === "closed") return { status: "done" };
  return { status: "action" };
}

export default function Recalls() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [branches, setBranches] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [tab, setTab] = useState("action");
  const [search, setSearch] = useState("");
  const [branchId, setBranchId] = useState("all");
  const [stallDays, setStallDays] = useState("all");
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [dismissing, setDismissing] = useState(null);

  const isAdmin = ["super_admin", "hq_admin", "franchisee_owner"].includes(user?.role);

  const load = useCallback(() => {
    setLoading(true);
    const params = { ...paramsForTab(tab), limit: 200 };
    if (search.trim()) params.search = search.trim();
    if (branchId !== "all") params.branch_id = branchId;
    if (stallDays !== "all") params.stall_days = Number(stallDays);
    api.get("/recalls", { params })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
    api.get("/recalls/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, [tab, search, branchId, stallDays]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    api.get("/users", { params: { role: "doctor" } }).then((r) => setDoctors(r.data)).catch(() => {});
  }, []);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const markContacted = (r) =>
    act(() => api.post(`/recalls/${r.id}/contacted`, { note: "" }), "Marked as contacted");
  const snooze = (r, days) =>
    act(() => api.post(`/recalls/${r.id}/snooze`, { days }), `Snoozed for ${days} days`);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile icon={AlarmClock} label="Overdue" value={summary.overdue} tone="text-red-600" />
        <Tile icon={CalendarCheck} label="Due today" value={summary.today} tone="text-navy" />
        <Tile icon={TrendingDown} label="Stalled" value={summary.stalled} tone="text-amber-700" />
        <Tile icon={Moon} label="Snoozed" value={summary.snoozed} tone="text-muted-foreground" />
      </div>

      {summary.unassigned_branch > 0 && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {summary.unassigned_branch} recall(s) belong to patients with no branch set, so branch staff
          cannot see them. Set a branch on those patients to route them.
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            {TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value} data-testid={`recall-tab-${t.value}`}>{t.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Name, mobile or ID…" value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-48 bg-white pl-9" data-testid="recalls-search"
            />
          </div>
          {tab === "stalled" && (
            <Select value={stallDays} onValueChange={setStallDays}>
              <SelectTrigger className="h-9 w-36 bg-white" data-testid="recall-stall-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any gap</SelectItem>
                {[30, 45, 60].map((d) => <SelectItem key={d} value={String(d)}>{d}+ days</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {isAdmin && branches.length > 1 && (
            <Select value={branchId} onValueChange={setBranchId}>
              <SelectTrigger className="h-9 w-40 bg-white" data-testid="recall-branch-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All branches</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {loading ? (
        <div className="rounded-xl border border-navy/10 bg-white py-16 text-center text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-navy/10 bg-white py-16 text-center">
          <PhoneOutgoing className="mx-auto mb-3 h-8 w-8 text-navy/20" />
          <p className="font-medium text-ink">Nobody to call right now</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Patients appear here when an advised visit comes due, or a treatment stalls with sessions left.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <RecallCard
              key={r.id} r={r}
              onOpenPatient={() => navigate(`/patients/${r.patient_id}`)}
              onContacted={() => markContacted(r)}
              onSnooze={(d) => snooze(r, d)}
              onBook={() => setBooking(r)}
              onDismiss={() => setDismissing(r)}
            />
          ))}
        </div>
      )}

      <BookDialog recall={booking} doctors={doctors} branches={branches}
        onClose={() => setBooking(null)} onDone={load} />
      <DismissDialog recall={dismissing} onClose={() => setDismissing(null)} onDone={load} />
    </div>
  );
}

function Tile({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-xl border border-navy/10 bg-white px-4 py-3 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        <Icon className="h-4 w-4 text-gold" />
      </div>
      <p className={`font-display text-2xl font-semibold ${tone}`}>{value ?? 0}</p>
    </div>
  );
}

function RecallCard({ r, onOpenPatient, onContacted, onSnooze, onBook, onDismiss }) {
  const overdue = r.urgency === "overdue";
  const closed = ["done", "dismissed"].includes(r.status);
  const stalled = r.reason === "stalled_treatment";

  return (
    <div
      className={`flex flex-wrap items-center gap-3 rounded-xl border bg-white px-4 py-3 shadow-sm
        ${overdue && !closed ? "border-l-4 border-l-red-500 border-navy/10" : "border-navy/10"}`}
      data-testid={`recall-${r.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={onOpenPatient} className="font-semibold text-ink hover:text-navy hover:underline">
            {r.patient_name}
          </button>
          {r.patient_no && <span className="font-mono text-[11px] text-muted-foreground">{r.patient_no}</span>}
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
            stalled ? "bg-amber-100 text-amber-800" : "bg-indigo-100 text-indigo-800"}`}>
            {REASON_LABEL[r.reason] || r.reason}
          </span>
          {r.status === "contacted" && (
            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-800">
              Contacted{r.attempts > 1 ? ` ×${r.attempts}` : ""}
            </span>
          )}
          {r.status === "snoozed" && !r.snooze_elapsed && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
              Snoozed to {formatDate(r.snoozed_until)}
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {r.source?.treatment_name && <span>{r.source.treatment_name} · </span>}
          {r.sessions_total > 0 && <span>{r.sessions_done}/{r.sessions_total} sessions · </span>}
          {stalled
            ? <span>{r.days_since_last_session ?? "—"} days since last visit</span>
            : <span className={overdue ? "font-medium text-red-600" : ""}>{smartDueLabel(r.due_at)}</span>}
          {r.patient_mobile && <span> · {maskPhone(r.patient_mobile)}</span>}
        </p>
        {r.advised_notes && (
          <p className="mt-1 truncate text-xs italic text-muted-foreground" title={r.advised_notes}>
            “{r.advised_notes}”
          </p>
        )}
        {closed && r.outcome && (
          <p className="mt-1 text-xs text-muted-foreground">
            Closed — {r.outcome.replace(/_/g, " ")}{r.auto_closed ? " (automatic)" : ""}
          </p>
        )}
      </div>

      {!closed && (
        <div className="flex flex-wrap items-center gap-1.5">
          {r.patient_mobile && (
            <Button asChild size="sm" variant="outline" className="h-8 border-navy/20 text-navy">
              <a href={`tel:${r.patient_mobile}`} data-testid={`recall-call-${r.id}`}>
                <PhoneCall className="mr-1 h-3.5 w-3.5" /> Call
              </a>
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={onContacted}
            className="h-8 border-navy/20 text-navy" data-testid={`recall-contacted-${r.id}`}>
            Contacted
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline" className="h-8 border-navy/20 text-navy" data-testid={`recall-snooze-${r.id}`}>
                <Clock className="mr-1 h-3.5 w-3.5" /> Snooze
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-36 p-1">
              {[7, 14, 30].map((d) => (
                <button key={d} onClick={() => onSnooze(d)}
                  className="w-full rounded px-2 py-1.5 text-left text-sm hover:bg-nude/40">
                  {d} days
                </button>
              ))}
            </PopoverContent>
          </Popover>
          <Button size="sm" onClick={onBook} className="h-8 bg-navy text-white hover:bg-navy-hover" data-testid={`recall-book-${r.id}`}>
            <CalendarPlus className="mr-1 h-3.5 w-3.5" /> Book
          </Button>
          <Button size="sm" variant="ghost" onClick={onDismiss}
            className="h-8 text-muted-foreground hover:text-red-600" data-testid={`recall-dismiss-${r.id}`}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

function BookDialog({ recall, doctors, branches, onClose, onDone }) {
  const [when, setWhen] = useState("");
  const [doctorId, setDoctorId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (recall) {
      setWhen("");
      setDoctorId(recall.source?.doctor_id || "");
      setBranchId(recall.branch_id || "");
    }
  }, [recall]);

  if (!recall) return null;

  const submit = async () => {
    if (!when) { toast.error("Pick a date and time"); return; }
    setSaving(true);
    try {
      await api.post(`/recalls/${recall.id}/book`, {
        scheduled_at: new Date(when).toISOString(),
        doctor_id: doctorId || null,
        branch_id: branchId || null,
        service: recall.source?.treatment_name || "",
      });
      toast.success("Appointment booked");
      onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!recall} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle className="font-display text-navy">Book Appointment</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="rounded-lg bg-cream/60 px-3 py-2.5">
            <p className="text-sm font-semibold text-ink">{recall.patient_name}</p>
            <p className="text-xs text-muted-foreground">
              {recall.patient_no} {recall.source?.treatment_name ? `· ${recall.source.treatment_name}` : ""}
            </p>
          </div>
          <Field label="Date & time *">
            <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="recall-book-when" />
          </Field>
          <Field label="Doctor">
            <Select value={doctorId} onValueChange={setDoctorId}>
              <SelectTrigger data-testid="recall-book-doctor"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>{doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {branches.length > 1 && (
            <Field label="Branch">
              <Select value={branchId} onValueChange={setBranchId}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-navy text-white hover:bg-navy-hover" data-testid="recall-book-confirm">
            {saving ? "Booking…" : "Book & Close Recall"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DismissDialog({ recall, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (recall) setReason(""); }, [recall]);
  if (!recall) return null;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/recalls/${recall.id}/dismiss`, { reason });
      toast.success("Dismissed");
      onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!recall} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle className="font-display text-navy">Dismiss Recall</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            {recall.patient_name} will not come back on this list for this reason.
          </p>
          <Field label="Why? (optional)">
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Not interested / moved city / already treated elsewhere" data-testid="recall-dismiss-reason" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-navy text-white hover:bg-navy-hover" data-testid="recall-dismiss-confirm">
            {saving ? "Saving…" : "Dismiss"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
